import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { selectProjectResults } from '../../project-details/data-access/project.reducer';
import PromptInjectionChart from './PromptInjectionChart';

// Define the interface for fairness data
export interface ComplianceAnalysis {
  'Internal Violations': number;
  'External Violations': number;
}

export interface SecurityFindings {
  'Adversarial Patterns': number;
  'Model Override Attempts': number;
}

export const PromptInjectionCard = () => {
  const projectResults = useSelector(selectProjectResults); // Fetch from Redux Store

  const [promptData, setPromptData] = useState<ComplianceAnalysis | null>(null);
  const [securityFindings, setSecurityFindings] = useState<SecurityFindings | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    console.log('projectResults', projectResults);
    if (projectResults) {
      setIsLoading(false);
      const resultsPromptInjection = projectResults?.results?.prompt_injection;

      if (Array.isArray(resultsPromptInjection) && resultsPromptInjection.length > 0) {
        // Find latest entry with compliance fields
        const latestEntry = resultsPromptInjection.reduce(
          (latest: any, curr: any) => {
            const hasData = curr['Prompt Compliance Analysis'] || curr['Security Findings'];
            if (!hasData) return latest;
            const latestTs = latest?.timestamp?.$date || latest?.timestamp || '';
            const currTs = curr?.timestamp?.$date || curr?.timestamp || '';
            return currTs >= latestTs ? curr : latest;
          },
          resultsPromptInjection[resultsPromptInjection.length - 1]
        );

        if (latestEntry['Prompt Compliance Analysis'] || latestEntry['Security Findings']) {
          setPromptData(latestEntry['Prompt Compliance Analysis'] || null);
          setSecurityFindings(latestEntry['Security Findings'] || null);
          return;
        }
      }

      // Fallback
      if (projectResults?.results?.prompt_validation) {
        const analysis = projectResults.results.prompt_validation['Prompt Compliance Analysis'] || null;
        const securityFindings = projectResults.results.prompt_validation['Security Findings'] || null;
        setPromptData(analysis);
        setSecurityFindings(securityFindings);
      }
    }
  }, [projectResults]);

  const chartData = promptData
    ? [
        {
          name: 'Internal Violations',
          value: promptData['Internal Violations']
        },
        {
          name: 'External Violations',
          value: promptData['External Violations']
        }
      ]
    : [];

  const securityFindingsChartData = securityFindings
    ? [
        {
          name: 'Adversarial Patterns',
          value: securityFindings['Adversarial Patterns'].toString().toLowerCase().includes('no') ? 0 : 1
        },
        {
          name: 'Model Override Attempts',
          value: securityFindings['Model Override Attempts'].toString().toLowerCase().includes('no') ? 0 : 1
        }
      ]
    : [];
  console.log('PROMPT INJECTION LOADING', isLoading);
  return (
    <>
      {isLoading && !projectResults ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <CircularProgress />
        </div>
      ) : (
        <Row className="g-3 mt-4">
          <Col xs={12} md={6}>
            {chartData && chartData.length > 0 && (
              <PromptInjectionChart
                description={
                  'Identifies and quantifies bias in ML models and LLM outputs by analyzing sensitive attributes.'
                }
                title={'Prompt Validation - Compliance Analysis'}
                data={chartData}
              />
            )}
          </Col>
          <Col xs={12} md={6}>
            {securityFindingsChartData && securityFindingsChartData.length > 0 && (
              <PromptInjectionChart
                description={
                  'Identifies and quantifies bias in ML models and LLM outputs by analyzing sensitive attributes.'
                }
                title={'Prompt Validation - Security Findings'}
                data={securityFindingsChartData}
              />
            )}
          </Col>
        </Row>
      )}
    </>
  );
};
